// API Service Layer - Connects frontend to backend APIs

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = sessionStorage.getItem('auth_token');
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

// Generic API request handler
const apiRequest = async (endpoint, options = {}) => {
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  
  return response.json();
};

// ============ AUTH APIs ============

export const authApi = {
  // Validate token from URL
  validate: async (token) => {
    return apiRequest('/api/auth/validate', {
      method: 'POST',
      body: JSON.stringify({ token })
    });
  },
  
  // Get current user info
  me: async () => {
    return apiRequest('/api/auth/me');
  }
};

// ============ CUSTOMER APIs ============

export const customerApi = {
  // Get all customers (filtered by role on backend)
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.division) params.append('division', filters.division);
    if (filters.status) params.append('status', filters.status);
    if (filters.account_manager) params.append('account_manager', filters.account_manager);
    
    const queryString = params.toString();
    return apiRequest(`/api/customers${queryString ? `?${queryString}` : ''}`);
  },
  
  // Get single customer with products
  getOne: async (compositeId) => {
    // URL encode the composite_id since it contains | characters
    const encodedId = encodeURIComponent(compositeId);
    return apiRequest(`/api/customers/${encodedId}`);
  },
  
  // Get customer products with filters (uses query parameter for composite_id)
  getProducts: async (compositeId, filters = {}) => {
    const params = new URLSearchParams();
    params.append('composite_id', compositeId);  // Required parameter
    if (filters.pipeline) params.append('pipeline', filters.pipeline);
    if (filters.product_group) params.append('product_group', filters.product_group);
    if (filters.product_name) params.append('product_name', filters.product_name);
    if (filters.list1) params.append('list1', filters.list1);
    if (filters.list2) params.append('list2', filters.list2);
    
    return apiRequest(`/api/customer-products?${params.toString()}`);
  },
  
  // Merge invalid customer code entries into a target customer
  merge: async (sourceCompositeIds, targetCompositeId) => {
    return apiRequest('/api/customers/merge', {
      method: 'POST',
      body: JSON.stringify({
        source_composite_ids: sourceCompositeIds,
        target_composite_id: targetCompositeId
      })
    });
  },
  createMother: async (sourceCompositeIds, motherName) => {
    return apiRequest('/api/customers/create-mother', {
      method: 'POST',
      body: JSON.stringify({
        source_composite_ids: sourceCompositeIds,
        mother_name: motherName
      })
    });
  },
  getMotherCustomers: async () => apiRequest('/api/customers/mother-customers'),
  
  // Get all valid customers for merge target dropdown
  getMergeTargets: async () => {
    return apiRequest('/api/customers/merge-targets');
  },
  
  // Update customer division
  updateDivision: async (custCode, division) => {
    return apiRequest(`/api/customers/${custCode}/division`, {
      method: 'PUT',
      body: JSON.stringify({ division })
    });
  },
  
  // Transfer customer to another account manager
  transfer: async (compositeId, newManagerEmpId, newManagerName) => {
    return apiRequest(`/api/customers/transfer`, {
      method: 'PUT',
      body: JSON.stringify({
        composite_id: compositeId,
        new_manager_emp_id: newManagerEmpId,
        new_manager_name: newManagerName
      })
    });
  }
};

// ============ MASTER DATA APIs ============

export const masterApi = {
  // Push master data (Admin only)
  push: async (data) => {
    return apiRequest('/api/master/push', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },
  
  // Update single product budget
  updateProduct: async (product) => {
    return apiRequest('/api/master/product', {
      method: 'PUT',
      body: JSON.stringify(product)
    });
  },
  
  // Delete a product (any non-REPEAT DEAL)
  deleteProduct: async (compositeId, productCode, list1 = '', list2 = '', pipeline = '') => {
    const params = new URLSearchParams({
      composite_id: compositeId,
      product_code: productCode,
      list1: list1,
      list2: list2,
      pipeline: pipeline
    });
    return apiRequest(`/api/master/product?${params.toString()}`, {
      method: 'DELETE'
    });
  },
  
  // Get pipeline summary (line counts per pipeline) for customer drilldown
  getPipelineSummary: async (compositeId) => {
    const params = new URLSearchParams({ composite_id: compositeId });
    return apiRequest(`/api/customers/pipeline-summary?${params.toString()}`);
  },
  
  // Bulk delete all items of a specific pipeline for a customer
  deletePipelineItems: async (compositeId, pipeline) => {
    return apiRequest('/api/customers/pipeline-items', {
      method: 'DELETE',
      body: JSON.stringify({ composite_id: compositeId, pipeline })
    });
  },
  
  // Bulk delete ALL non-REPEAT DEAL items for a customer
  deleteAllItems: async (compositeId) => {
    return apiRequest('/api/customers/all-items', {
      method: 'DELETE',
      body: JSON.stringify({ composite_id: compositeId })
    });
  },

  // Create a new deal/customer card for budget entry
  createDeal: async (dealName, division, empId, isNoCustomer = false) => {
    return apiRequest('/api/customers/create-deal', {
      method: 'POST',
      body: JSON.stringify({
        deal_name: dealName,
        division,
        emp_id: empId,
        is_no_customer: isNoCustomer
      })
    });
  }
};

// ============ APPROVAL APIs ============

export const approvalApi = {
  // Approve a customer's budget
  approve: async (custCode, approvedByEmpId, approvedByName) => {
    return apiRequest('/api/approve', {
      method: 'POST',
      body: JSON.stringify({
        cust_code: custCode,
        approved_by_emp_id: approvedByEmpId,
        approved_by_name: approvedByName
      })
    });
  },
  
  // Get all approved budgets
  getApproved: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.division) params.append('division', filters.division);
    if (filters.account_manager) params.append('account_manager', filters.account_manager);
    const queryString = params.toString();
    return apiRequest(`/api/approved${queryString ? `?${queryString}` : ''}`);
  },

  // Approve all budgets for a manager+division
  approveAll: async (empId, division) => {
    return apiRequest('/api/approve-all', {
      method: 'POST',
      body: JSON.stringify({ emp_id: empId, division })
    });
  },

  // Mark a manager+division as reviewed
  reviewManager: async (empId, division) => {
    return apiRequest('/api/managers/review', {
      method: 'POST',
      body: JSON.stringify({ emp_id: empId, division })
    });
  },

  // Get reviewed managers
  getReviewed: async () => apiRequest('/api/managers/reviewed'),

  // Get pipeline-wise KPIs for a manager+division (from line items)
  getManagerPipelineKpis: async (empId, division) => {
    const params = new URLSearchParams({ emp_id: empId, division });
    return apiRequest(`/api/manager-pipeline-kpis?${params.toString()}`);
  },

  // Set pipeline target % (admin)
  setPipelineTarget: async (empId, division, pipeline, targetPct) => {
    return apiRequest('/api/pipeline-targets', {
      method: 'POST',
      body: JSON.stringify({ emp_id: empId, division, pipeline, target_pct: targetPct })
    });
  },

  // Get pipeline targets
  getPipelineTargets: async (empId, division) => {
    const params = new URLSearchParams();
    if (empId) params.append('emp_id', empId);
    if (division) params.append('division', division);
    return apiRequest(`/api/pipeline-targets?${params.toString()}`);
  },

  // Add new manager (admin)
  addManager: async (name, division, empId) => {
    return apiRequest('/api/managers/add', {
      method: 'POST',
      body: JSON.stringify({ name, division, emp_id: empId || '' })
    });
  },
};

// ============ LOOKUP APIs ============

export const lookupApi = {
  getDivisions: async () => apiRequest('/api/lookup/divisions'),
  getDivisionStats: async () => apiRequest('/api/lookup/division-stats'),
  getManagerStats: async (division = null) => {
    const params = division ? `?division=${division}` : '';
    return apiRequest(`/api/lookup/manager-stats${params}`);
  },
  getAccountManagers: async (division = null) => {
    const params = division ? `?division=${division}` : '';
    return apiRequest(`/api/lookup/account-managers${params}`);
  },
  getPipelines: async () => apiRequest('/api/lookup/pipelines'),
  getProductGroups: async (division = null) => {
    const params = division ? `?division=${division}` : '';
    return apiRequest(`/api/lookup/product-groups${params}`);
  },
  getProducts: async (division = null, productGroup = null) => {
    const params = new URLSearchParams();
    if (division) params.append('division', division);
    if (productGroup) params.append('product_group', productGroup);
    const queryString = params.toString();
    return apiRequest(`/api/lookup/products${queryString ? `?${queryString}` : ''}`);
  },
  getList1: async (division = null) => {
    const params = division ? `?division=${division}` : '';
    return apiRequest(`/api/lookup/list1${params}`);
  },
  getList2: async (division = null) => {
    const params = division ? `?division=${division}` : '';
    return apiRequest(`/api/lookup/list2${params}`);
  },
  getProductRate: async (productCode, custCode, list1 = '', list2 = '') => {
    const params = new URLSearchParams();
    if (productCode) params.append('product_code', productCode);
    if (custCode) params.append('cust_code', custCode);
    params.append('list1', list1 || '');
    params.append('list2', list2 || '');
    return apiRequest(`/api/lookup/product-rate?${params.toString()}`);
  },
  // Add custom division
  addDivision: async (divisionName) => {
    return apiRequest('/api/divisions/add', {
      method: 'POST',
      body: JSON.stringify({ division: divisionName })
    });
  },
  getCustomDivisions: async () => apiRequest('/api/divisions/custom'),
};

// ============ STATS APIs ============

export const statsApi = {
  getSummary: async () => apiRequest('/api/stats/summary'),
  getGlobalStats: async () => apiRequest('/api/stats/global'),
  getUserStats: async (division = null) => {
    const params = division ? `?division=${encodeURIComponent(division)}` : '';
    return apiRequest(`/api/stats/user${params}`);
  }
};

// ============ LOCK APIs ============

export const lockApi = {
  acquire: async (compositeId) => apiRequest('/api/locks/acquire', {
    method: 'POST',
    body: JSON.stringify({ composite_id: compositeId })
  }),
  release: async (compositeId) => apiRequest('/api/locks/release', {
    method: 'POST',
    body: JSON.stringify({ composite_id: compositeId })
  }),
  heartbeat: async (compositeId) => apiRequest('/api/locks/heartbeat', {
    method: 'POST',
    body: JSON.stringify({ composite_id: compositeId })
  }),
  status: async (compositeId) => {
    const encodedId = encodeURIComponent(compositeId);
    return apiRequest(`/api/locks/status/${encodedId}`);
  }
};

// ============ AUDIT LOG APIs ============

export const auditApi = {
  getLogs: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.action) params.append('action', filters.action);
    if (filters.cust_code) params.append('cust_code', filters.cust_code);
    if (filters.composite_id) params.append('composite_id', filters.composite_id);
    if (filters.user_emp_id) params.append('user_emp_id', filters.user_emp_id);
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    return apiRequest(`/api/audit/logs?${params.toString()}`);
  }
};

// ============ HEALTH CHECK ============

export const healthApi = {
  check: async () => apiRequest('/api/health')
};

// ============ ADMIN APIs ============

export const adminApi = {
  syncToMySQL: async (division = null) => apiRequest('/api/admin/sync-to-mysql' + (division ? ('?division=' + encodeURIComponent(division)) : ''), { method: 'POST' }),
  clearCache: async () => apiRequest('/api/admin/clear-cache', { method: 'POST' })
};

// ============ EXPORT APIs ============

export const exportApi = {
  downloadDataDump: (empId, division) => {
    const token = sessionStorage.getItem('auth_token');
    const url = `${API_URL}/api/export/data-dump?emp_id=${encodeURIComponent(empId)}&division=${encodeURIComponent(division)}&token=${encodeURIComponent(token || '')}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${empId}_${division}_DataDump.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
};

// Export all APIs
export default {
  auth: authApi,
  customer: customerApi,
  master: masterApi,
  approval: approvalApi,
  lookup: lookupApi,
  stats: statsApi,
  lock: lockApi,
  audit: auditApi,
  health: healthApi,
  export: exportApi
};